import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { StatusGlyph } from '../../components/StatusGlyph';
import { ApiError, api, type AdminCustomer } from '../../lib/api';
import { formatDate, formatDateTime, plural } from '../../lib/format';
import { ActivityTable } from './ActivityTable';

/**
 * One carrier.
 *
 * Aggregate throughout: requirement gaps are counted by requirement code, never listed by
 * driver. "Nine drivers are missing an annual MVR" is what a support conversation and a
 * product decision are both made of; naming those nine is the carrier's business, and
 * reaching it means opening their workspace — the button at the bottom of this page, which
 * is read-only, expires in an hour, and writes itself into their own audit trail.
 */

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'customer', id],
    queryFn: () => api.get<AdminCustomer>(`/api/admin/tenants/${id}`),
    enabled: !!id,
  });

  /*
   * The Customers row menu links here with #open-workspace, because the reason for entering a
   * carrier's workspace is mandatory and there should be exactly one form that collects it.
   * A client-side route change does not honour a fragment on its own, and the target only exists
   * once the query has resolved — hence waiting on `data` rather than scrolling on mount.
   */
  const location = useLocation();
  useEffect(() => {
    if (!data || location.hash !== '#open-workspace') return;
    document
      .getElementById('open-workspace')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data, location.hash]);

  if (isPending) return <p className="muted">Loading…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;
  if (!data) return null;

  const { tenant: t, gaps, staff, activity } = data;
  const evaluated = t.lastEvaluatedFor !== null;

  return (
    <div>
      <header className="admin-head">
        <p className="eyebrow">
          <Link to="/">Customers</Link> · {t.slug}
        </p>
        <h1 className="admin-title">{t.name}</h1>
        <p className="cite">
          {t.dotNumber ? `USDOT ${t.dotNumber} · ` : ''}
          {t.timezone} · workspace {t.status} · billing {t.subscriptionStatus} · {t.seats}{' '}
          {plural(t.seats, 'seat')} · since {formatDate(t.createdAt)}
        </p>
        <p className="cite">
          {/* The digest recipient — the carrier's own contact, never a driver address. */}
          contact {t.alertEmail ?? 'not set'} · account owner{' '}
          {t.accountOwner ?? 'unassigned'}
          {t.trialEndsAt ? ` · trial ends ${formatDate(t.trialEndsAt)}` : ''}
        </p>

        <p className="admin-ledger">
          {!evaluated ? (
            <>This carrier has never been evaluated by the nightly scan.</>
          ) : (
            <>
              <span className={`num ${t.driversNonCompliant > 0 ? 'tone-bad' : 'tone-ok'}`}>
                {t.driversNonCompliant}
              </span>{' '}
              of {t.driversEvaluated} evaluated {plural(t.driversEvaluated, 'driver')}{' '}
              {t.driversNonCompliant === 1 ? 'has a file that' : 'have files that'} would not
              survive an audit, as of {formatDate(t.lastEvaluatedFor)}.
            </>
          )}
        </p>
      </header>

      {t.alertsBounced7d > 0 && (
        <div className="stale-notice" role="status">
          <strong>{t.alertsBounced7d} alert deliveries failed in the last 7 days.</strong>
          <span>
            This carrier may believe they are being warned about expiries when they are not.
          </span>
        </div>
      )}

      {/* ── Where the file is failing ───────────────────────────────────────── */}

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>What is deficient</h2>
          <span className="cite">
            by requirement · {evaluated ? `as of ${formatDate(t.lastEvaluatedFor)}` : 'not evaluated'}
          </span>
        </div>

        <div className="card scroll-x">
          <table>
            <thead>
              <tr>
                <th>Requirement</th>
                <th>Citation</th>
                <th>Status</th>
                <th className="num-cell">Drivers</th>
              </tr>
            </thead>
            <tbody>
              {gaps.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    {evaluated
                      ? 'Nothing expired, missing, awaiting review, or expiring within 60 days.'
                      : 'No evaluation has been stored for this carrier yet.'}
                  </td>
                </tr>
              )}
              {gaps.map((g) => (
                <tr key={`${g.requirementCode}-${g.status}`}>
                  <td>
                    <strong>{g.title}</strong>
                    <br />
                    <span className="cite">{g.requirementCode}</span>
                  </td>
                  {/* The citation is always shown, so a carrier can check our work. */}
                  <td className="cite">{g.cfrCite ?? '—'}</td>
                  <td>
                    <StatusGlyph status={g.status} />
                  </td>
                  <td className="num-cell tnum">{g.driverCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Who uses it ─────────────────────────────────────────────────────── */}

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>Staff accounts</h2>
          <span className="cite">
            {t.driverLoginsActive} driver {plural(t.driverLoginsActive, 'login')} not listed
          </span>
        </div>

        <div className="card scroll-x">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last sign-in</th>
                <th className="num-cell">Live sessions</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.fullName}</strong>
                  </td>
                  <td>{u.email}</td>
                  <td className="cite">{u.role.replace('_', ' ')}</td>
                  <td className="tnum">
                    {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : <span className="muted">never</span>}
                  </td>
                  <td className="num-cell tnum">{u.activeSessions}</td>
                  <td>
                    {u.deactivatedAt ? (
                      <span className="pill">deactivated</span>
                    ) : u.lockedUntil && Date.parse(u.lockedUntil) > Date.now() ? (
                      <span className="pill is-bad">locked</span>
                    ) : u.failedLoginCount > 0 ? (
                      <span className="pill is-warn">
                        {u.failedLoginCount} failed
                      </span>
                    ) : (
                      <span className="pill is-ok">active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Recent activity ─────────────────────────────────────────────────── */}

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>Recent activity</h2>
          <Link to={`/activity?tenantId=${t.id}`} className="cite">
            See all →
          </Link>
        </div>
        <ActivityTable events={activity.events} showTenant={false} />
      </section>

      <OpenWorkspace tenantId={t.id} tenantName={t.name} status={t.status} />
    </div>
  );
}

/**
 * Starts an impersonation.
 *
 * The reason box is not a formality: it is stored on the session, written into the
 * carrier's audit trail, and shown to anyone in their workspace while the session is live.
 * The copy says so, because a field whose consequences are invisible gets filled with "x".
 */
function OpenWorkspace({
  tenantId,
  tenantName,
  status,
}: {
  tenantId: string;
  tenantName: string;
  status: string;
}) {
  const [reason, setReason] = useState('');

  const open = useMutation({
    mutationFn: () =>
      api.post<{ tenant: { id: string }; expiresAt: string }>(
        `/api/admin/tenants/${tenantId}/impersonate`,
        { reason },
      ),
    onSuccess: () => {
      /*
       * Hard navigation, not a router push. The session cookie has just been swapped for
       * the workspace session, and every cached query in memory belongs to the console.
       * A full reload is the only way to be sure nothing from the previous identity is
       * still on screen.
       */
      window.location.href = '/';
    },
  });

  const unreachable = status === 'suspended' || status === 'canceled';

  return (
    <section className="admin-section" id="open-workspace">
      <div className="admin-section-head">
        <h2>Open this workspace</h2>
      </div>

      <div className="impersonate-box">
        <p>
          Opens {tenantName}'s workspace as yourself, read-only, for one hour. You will see
          what their safety manager sees — including driver names and documents — and can
          change nothing. Drug and alcohol testing records stay closed.
        </p>
        <p>
          This is recorded in <strong>{tenantName}'s own audit trail</strong>, with your name
          and the reason below, and a banner says so to anyone in the workspace while you are
          there. Describe the support case, not a driver.
        </p>

        {unreachable ? (
          <p className="field-error">
            This workspace is {status} and cannot be opened.
          </p>
        ) : (
          <>
            <div className="field">
              <label htmlFor="impersonation-reason">Reason</label>
              <input
                id="impersonation-reason"
                type="text"
                value={reason}
                placeholder="e.g. Ticket 412 — digest email not arriving"
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div className="impersonate-actions">
              <button
                type="button"
                className="btn"
                disabled={reason.trim().length < 12 || open.isPending}
                onClick={() => open.mutate()}
              >
                {open.isPending ? 'Opening…' : 'Open workspace'}
              </button>
              {reason.trim().length > 0 && reason.trim().length < 12 && (
                <span className="cite">A little more detail than that.</span>
              )}
            </div>
          </>
        )}

        {open.error && (
          <p className="field-error">
            {open.error instanceof ApiError
              ? open.error.message
              : 'Could not open the workspace.'}
          </p>
        )}
      </div>
    </section>
  );
}
